import { useState, useEffect, useRef } from 'react'
import { useServices } from 'cng-web-lib'
import { FileForUserGetPartyId, FileForUserGetCurrentLoginId } from 'src/common/FileForUserCommon'
import TprApiUrls from 'src/apiUrls/TprApiUrls'
import UserProfileApiUrls from 'src/apiUrls/UserProfileApiUrls'
import { MPCI_ADMIN_ORG_ID } from '../constants/mpciConstants'

/**
 * useOrgFilter
 *
 * Centralises the "admin sees all, others see own (+ children for parent-corp)" logic for MPCI.
 * Fetches org list from TPR service (same as other modules: POST TprApiUrls.GET).
 *
 * Fetch strategy:
 *   admin       — fetch ALL orgs (size: 9999) for the org filter dropdown
 *   parent-corp — fetch own org id + child/grandchild org ids (shared
 *                 getServiceBureauPartyIdByUserName lookup — getAllChildPartyIdByUsername
 *                 wrongly returns nothing when called for a grandchild login, so this is used
 *                 for all tiers instead), then one lightweight single-record TPR call per id,
 *                 merged into orgList
 *   plain org   — fetch ONLY their own org by id, lightweight single-record call
 *
 * Returns:
 *   isAdmin          {boolean}       — true when logged-in party === MPCI_ADMIN_ORG_ID
 *   isParentCorp     {boolean}       — true when the shared lookup returns 1+ child org ids
 *   canActAsOtherOrg {boolean}       — isAdmin || isParentCorp
 *   orgList          {Array}         — [{ id, name }] — always populated (used for name lookup in datatables)
 *   selectedOrgId    {number|null}   — null = All Organisations (admin) / unset (parent-corp, falls back to own org)
 *   setSelectedOrgId {fn}
 *   effectiveOrgId   {number|null}   — resolved orgId to send to BE:
 *                                      null  = admin selected "All" (no filter)
 *                                      own partyId = plain org / parent-corp with nothing selected
 *                                      selected child partyId = parent-corp acting as a child org
 *   defaultOrgIds    {Array|null}    — org scope for LIST views when nothing is explicitly
 *                                      picked from an org filter: null for admin (no filter,
 *                                      see all), [own, ...children] for parent-corp (own org
 *                                      alone until the async child-party/TPR lookup resolves,
 *                                      then widens automatically), [own] for a plain org.
 *   loggedInOrgId    {number|null}   — the caller's own party id, regardless of role/selection.
 *                                      Unlike effectiveOrgId (which is null for admin until they
 *                                      pick), this is always the current user's own identity —
 *                                      used to default "Filing For" pickers for every role.
 *   orgFilterLoading {boolean}       — true until org scope has fully resolved. Callers doing an
 *                                      org-scoped list fetch should wait for this to go false
 *                                      before firing their initial request, else they'll fire
 *                                      against the narrow [loggedInOrgId] placeholder and then
 *                                      again once the real (possibly wider) scope resolves.
 *
 * @param {object} [opts]
 * @param {object} [opts.preloaded] — an already-resolved useOrgFilter() result from a caller
 *   higher up the tree (e.g. the page-level TablePage). When given, this instance skips its own
 *   TPR/child-lookup fetch entirely and just returns `preloaded` — avoids MpciTable and its parent
 *   TablePage each independently fetching the same org list.
 */
function useOrgFilter(opts) {
  const preloaded = opts?.preloaded
  const { securedSendRequest } = useServices()
  // FileForUserGetPartyId() respects "file for user" impersonation.
  // Falls back to logged-in user's own partyId if no impersonation active.
  const loggedInOrgId = Number(FileForUserGetPartyId()) || null
  const isAdmin = loggedInOrgId === MPCI_ADMIN_ORG_ID

  const [orgList, setOrgList] = useState([])
  const [selectedOrgId, setSelectedOrgId] = useState(null) // null = "All" (admin) / unset (parent-corp)
  const [isParentCorp, setIsParentCorp] = useState(false)
  // True until the org scope (own id, or own+child+grandchild) has fully resolved. Callers
  // should hold off firing any org-scoped list fetch while this is true — defaultOrgIds is only
  // a narrow placeholder ([loggedInOrgId]) until then, and fetching against it produces a
  // premature request that gets immediately superseded once the real scope resolves.
  const [orgFilterLoading, setOrgFilterLoading] = useState(true)
  const mountedRef = useRef(true)

  // Fetches one org record per id (TPR single-EQUAL-filter call, same shape as the plain-org case)
  // and merges results into orgList as each resolves. Flips orgFilterLoading false once every
  // id's call has settled (success or error).
  function fetchOrgsByIds(ids) {
    const uniqueIds = [...new Set(ids)]
    setOrgList([])
    if (uniqueIds.length === 0) {
      setOrgFilterLoading(false)
      return
    }
    let pending = uniqueIds.length
    uniqueIds.forEach((id) => {
      securedSendRequest.execute(
        'POST',
        TprApiUrls.GET,
        {
          page: 0,
          size: 1,
          baseFilterDTO: {
            filterType: 'AND',
            filterProperties: [{ fieldName: 'id', operatorType: 'EQUAL', value1: id }],
            sortProperties: []
          }
        },
        (response) => {
          if (!mountedRef.current) return
          const content = response?.data?.content ?? []
          const orgs = content.map((p) => ({ id: p.id, name: p.name }))
          if (orgs.length) {
            setOrgList((prev) => (prev.some((o) => o.id === orgs[0].id) ? prev : [...prev, ...orgs]))
          }
          pending -= 1
          if (pending === 0) setOrgFilterLoading(false)
        },
        (err) => {
          if (!mountedRef.current) return
          console.error('[useOrgFilter] failed to load org from TPR', id, err)
          pending -= 1
          if (pending === 0) setOrgFilterLoading(false)
        }
      )
    })
  }

  useEffect(() => {
    if (preloaded) return // caller already fetched this — don't duplicate the network calls
    mountedRef.current = true

    if (isAdmin) {
      securedSendRequest.execute(
        'POST',
        TprApiUrls.GET,
        { page: 0, size: 9999 },
        (response) => {
          if (!mountedRef.current) return
          const content = response?.data?.content ?? []
          setOrgList(content.map((p) => ({ id: p.id, name: p.name })))
          setOrgFilterLoading(false)
        },
        (err) => {
          if (!mountedRef.current) return
          console.error('[useOrgFilter] failed to load org list from TPR', err)
          setOrgFilterLoading(false)
        }
      )
      return () => { mountedRef.current = false }
    }

    // Non-admin — check whether this user is a parent-corp (has child/grandchild orgs) via
    // the shared service-bureau lookup. getAllChildPartyIdByUsername returns nothing when the
    // caller is a grandchild login, so getServiceBureauPartyIdByUserName is used for all tiers.
    securedSendRequest.execute(
      'POST',
      UserProfileApiUrls.SB_PARTYID_LIST,
      { loginId: FileForUserGetCurrentLoginId() },
      (response) => {
        if (!mountedRef.current) return
        const childIds = (response?.data ?? []).filter((id) => id != null && id !== loggedInOrgId)
        if (childIds.length > 0) {
          setIsParentCorp(true)
          fetchOrgsByIds([loggedInOrgId, ...childIds])
        } else {
          setIsParentCorp(false)
          fetchOrgsByIds([loggedInOrgId])
        }
      },
      (err) => {
        if (!mountedRef.current) return
        console.error('[useOrgFilter] failed to load child party ids', err)
        setIsParentCorp(false)
        fetchOrgsByIds([loggedInOrgId])
      }
    )

    return () => { mountedRef.current = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const canActAsOtherOrg = isAdmin || isParentCorp

  // admin: selected or null (all). parent-corp: selected or own org (never "all"). plain org: always own org.
  const effectiveOrgId = isAdmin
    ? selectedOrgId
    : isParentCorp
      ? (selectedOrgId ?? loggedInOrgId)
      : loggedInOrgId

  // Default LIST scope when nothing is explicitly picked from an org filter. For parent-corp
  // this is [own, ...children] (orgList) rather than just the own org — orgList only resolves
  // after the async child-party/TPR fetch completes, so it's [loggedInOrgId] alone until then.
  const defaultOrgIds = isAdmin
    ? null
    : orgList.length > 0
      ? orgList.map((o) => o.id)
      : (loggedInOrgId != null ? [loggedInOrgId] : [])

  return preloaded ?? { isAdmin, isParentCorp, canActAsOtherOrg, orgList, selectedOrgId, setSelectedOrgId, effectiveOrgId, defaultOrgIds, loggedInOrgId, orgFilterLoading }
}

export default useOrgFilter
